import { INITIAL_VOICE_STATE, VoiceBridgeState } from '../UnityGame/localVoiceBridge';
import { SpatialSnapshot } from './protocol';
import { voiceGain } from './voiceGain';

export interface VoiceCredentials { app_id: string; token: string; channel: string; uid: number }
let sdkLoad: Promise<any> | undefined;
function loadSdk(baseUrl: string): Promise<any> {
  if ((window as any).AgoraRTC) return Promise.resolve((window as any).AgoraRTC);
  if (!sdkLoad) sdkLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${baseUrl}/AgoraRTC_N-4.21.0.js`;
    script.onload = () => resolve((window as any).AgoraRTC);
    script.onerror = () => { sdkLoad = undefined; reject(new Error('Voice could not load')); };
    document.body.appendChild(script);
  });
  return sdkLoad;
}

/** Agora channel/uid are issued by ff-api, shared with UPC. No second microphone stream. */
export class BrowserVoice {
  private state: VoiceBridgeState = { ...INITIAL_VOICE_STATE, ready: true, status: 'disconnected' };
  private client: any;
  private microphone: any;
  private sdk: any;
  private disposed = false;
  private connecting = false;
  private operation: Promise<void> = Promise.resolve();
  private mutedUsers = new Set<string>();
  constructor(private baseUrl: string, private credentials: VoiceCredentials, private refresh: () => Promise<VoiceCredentials>, private audible: number, private conversational: number) {}
  private emit() {
    if (!this.disposed) window.dispatchEvent(new CustomEvent('VoiceStateChanged', { detail: JSON.stringify(this.state) }));
  }
  async connect() {
    if (this.connecting || this.state.connected || this.disposed) return;
    this.connecting = true; this.state.status = 'connecting'; this.emit();
    try {
      this.sdk = await loadSdk(this.baseUrl);
      if (this.disposed) return;
      if (this.client) {
        this.microphone?.close(); this.microphone = undefined; this.state.muted = true;
        await this.client.leave(); this.client.removeAllListeners();
        this.credentials = await this.refresh();
        if (this.disposed) return;
      }
      this.client = this.sdk.createClient({ mode: 'rtc', codec: 'vp8' });
      this.client.on('user-published', async (user: any, mediaType: string) => {
        if (mediaType !== 'audio' || this.disposed) return;
        try { await this.client.subscribe(user, 'audio'); user.audioTrack?.setVolume(0); user.audioTrack?.play(); } catch { /* subscription may race a departure */ }
      });
      this.client.on('token-privilege-will-expire', async () => {
        try { const next = await this.refresh(); if (!this.disposed) await this.client.renewToken(next.token); } catch { this.state.status = 'disconnected'; this.emit(); }
      });
      this.client.on('connection-state-change', (state: string) => {
        if (this.disposed) return;
        this.state.connected = state === 'CONNECTED';
        this.state.status = state === 'CONNECTED' ? 'connected' : state === 'CONNECTING' || state === 'RECONNECTING' ? 'connecting' : 'disconnected';
        this.emit();
      });
      await this.client.join(this.credentials.app_id, this.credentials.channel, this.credentials.token || null, this.credentials.uid);
      if (this.disposed) { await this.client.leave(); return; }
      this.state.channel = this.credentials.channel; this.state.connected = true; this.state.status = 'connected';
      // Joining/listening does not prompt for microphone permission. Unmute does.
      await this.devices();
      this.emit();
    } catch { this.state.connected = false; this.state.status = 'disconnected'; this.emit(); }
    finally { this.connecting = false; }
  }
  private async devices() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    this.state.inputDevices = devices.filter(d => d.kind === 'audioinput').map(d => d.label || d.deviceId);
    this.state.outputDevices = devices.filter(d => d.kind === 'audiooutput').map(d => d.label || d.deviceId);
  }
  command(command: any) {
    if (command.action === 'getState') { this.emit(); return; }
    if (command.action === 'connect') { void this.connect(); return; }
    this.operation = this.operation.then(async () => {
      if (this.disposed) return;
      if (command.action === 'setMuted') {
        const muted = Boolean(command.muted);
        if (!muted && !this.state.connected) { this.emit(); return; }
        if (!muted && !this.microphone) {
          const microphone = await this.sdk.createMicrophoneAudioTrack({ AEC: true, ANS: true, AGC: true });
          if (this.disposed) { microphone.close(); return; }
          try { await this.client.publish(microphone); }
          catch (error) { microphone.close(); throw error; }
          if (this.disposed) { microphone.close(); return; }
          this.microphone = microphone;
          this.microphone.setVolume(this.state.inputVolume * 100);
          await this.devices();
        }
        await this.microphone?.setMuted(muted);
        this.state.muted = muted;
      } else if (command.action === 'setOutputVolume') this.state.outputVolume = Math.max(0, Math.min(1, Number(command.value) || 0));
      else if (command.action === 'setInputVolume') {
        this.state.inputVolume = Math.max(0, Math.min(1, Number(command.value) || 0));
        this.microphone?.setVolume(this.state.inputVolume * 100);
      } else if (command.action === 'setInputDevice' || command.action === 'setOutputDevice') {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const input = command.action === 'setInputDevice';
        const device = devices.find(d => d.kind === (input ? 'audioinput' : 'audiooutput') && (d.label === command.deviceName || d.deviceId === command.deviceName));
        if (device) {
          if (input) { await this.microphone?.setDevice(device.deviceId); this.state.inputDevice = command.deviceName; }
          else { for (const user of this.client?.remoteUsers || []) await user.audioTrack?.setPlaybackDevice(device.deviceId); this.state.outputDevice = command.deviceName; }
        }
      }
      this.emit();
    }).catch(() => {
      // Never display "muted" while an existing capture track may still publish.
      this.microphone?.close(); this.microphone = undefined;
      this.state.muted = true; this.emit();
    });
  }
  update(snapshot: SpatialSnapshot) {
    const self = snapshot.players.find(p => p.userId === snapshot.selfId);
    if (!self) return;
    const players = new Map(snapshot.players.map(p => [p.agoraUid, p]));
    for (const user of this.client?.remoteUsers || []) {
      const player = players.get(Number(user.uid));
      const position = player?.position;
      const distance = position ? Math.hypot(position.x - self.position.x, position.y - self.position.y, position.z - self.position.z) : Infinity;
      const sameRoom = player?.acousticRoom === self.acousticRoom;
      const gain = sameRoom ? voiceGain(distance, player?.voiceDistances?.audibleDistance ?? this.audible, player?.voiceDistances?.conversationalDistance ?? this.conversational) : 0;
      user.audioTrack?.setVolume(player && this.mutedUsers.has(player.userId) ? 0 : Math.round(gain * this.state.outputVolume * 100));
    }
  }
  setPlayerMuted(userId: string, muted: boolean) { if (muted) this.mutedUsers.add(userId); else this.mutedUsers.delete(userId); }
  async close() {
    this.disposed = true;
    this.microphone?.close();
    try { await this.client?.leave(); } catch { /* already disconnected */ }
    this.client?.removeAllListeners();
  }
}
